import React, { useEffect, useState, useCallback, useRef } from 'react';

/* ─── design tokens ─────────────────────────────────────────────── */
const C = {
  bg: '#FAFAF9', surface: '#FFFFFF', surface2: '#F6F6F4',
  ink1: '#0B1020', ink2: '#1F2937', ink3: '#475569', ink4: '#64748B', ink5: '#94A3B8',
  line: '#E7E5E4', line2: '#EFEDEA',
  indigo: '#4F46E5', indigo600: '#4338CA', indigo50: '#EEF2FF', indigo100: '#E0E7FF',
  emerald: '#059669', emerald50: '#ECFDF5',
  amber: '#D97706', amber50: '#FFFBEB',
  rose: '#E11D48', rose50: '#FFF1F2',
  shadow: '0 1px 2px rgba(15,23,42,.04), 0 6px 20px -8px rgba(15,23,42,.08)',
  shadowSm: '0 1px 2px rgba(15,23,42,.04)',
};

const f = {
  display: "'Inter Tight','Inter',sans-serif",
  body: "'Inter',system-ui,sans-serif",
  mono: "'JetBrains Mono',ui-monospace,monospace",
};

/* ─── helpers ────────────────────────────────────────────────────── */
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('tr-TR');
}
function fmtNum(n) { return Number(n || 0).toLocaleString(); }
function daysLeft(d) {
  if (!d) return null;
  return Math.ceil((new Date(d) - Date.now()) / 86400000);
}

/* ─── sparkline ─────────────────────────────────────────────────── */
function Spark({ points = [], color = C.indigo }) {
  if (!points.length) return null;
  const W = 58, H = 18;
  const max = Math.max(...points, 1);
  const x = i => (W * i) / (points.length - 1);
  const y = v => H - (H * v) / max;
  const pts = points.map((v, i) => `${x(i)},${y(v)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} preserveAspectRatio="none"
      style={{ position: 'absolute', right: 10, bottom: 8, opacity: .55 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── donut chart ────────────────────────────────────────────────── */
function Donut({ segments, centerV, centerK, size = 140 }) {
  const cx = size / 2, cy = size / 2, r = size * 0.37, ir = size * 0.257;
  const total = segments.reduce((s, x) => s + x.v, 0) || 1;
  let angle = 0;
  const paths = segments.map((seg, i) => {
    const frac = seg.v / total;
    if (frac <= 0) return null;
    if (frac >= 0.9999) {
      return <circle key={i} cx={cx} cy={cy} r={(r + ir) / 2} fill="none"
        stroke={seg.c} strokeWidth={r - ir} />;
    }
    const a0 = angle * 2 * Math.PI - Math.PI / 2;
    const a1 = (angle + frac) * 2 * Math.PI - Math.PI / 2;
    const cos0 = Math.cos(a0), sin0 = Math.sin(a0);
    const cos1 = Math.cos(a1), sin1 = Math.sin(a1);
    const large = frac > 0.5 ? 1 : 0;
    const d = [
      `M ${cx + r * cos0} ${cy + r * sin0}`,
      `A ${r} ${r} 0 ${large} 1 ${cx + r * cos1} ${cy + r * sin1}`,
      `L ${cx + ir * cos1} ${cy + ir * sin1}`,
      `A ${ir} ${ir} 0 ${large} 0 ${cx + ir * cos0} ${cy + ir * sin0}`,
      'Z',
    ].join(' ');
    angle += frac;
    return <path key={i} d={d} fill={seg.c} />;
  });
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      {paths}
      <text x={cx} y={cy - 3} textAnchor="middle"
        style={{ fontFamily: f.display, fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', fill: C.ink1 }}>
        {centerV}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle"
        style={{ fontFamily: f.mono, fontSize: 9, fill: C.ink4, textTransform: 'uppercase', letterSpacing: '.08em' }}>
        {centerK}
      </text>
    </svg>
  );
}

/* ─── trend SVG chart ────────────────────────────────────────────── */
function TrendChart({ data = [], style = 'area', width = 700 }) {
  const W = Math.max(width, 300), H = 220;
  const P = { l: 32, r: 10, t: 10, b: 24 };
  const shaped = data.map(p => p.count);
  if (!shaped.length) return <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.ink5, fontSize: 12 }}>No data</div>;
  const max = Math.max(...shaped, 1) * 1.2;
  const x = i => P.l + (W - P.l - P.r) * (i / (shaped.length - 1));
  const y = v => P.t + (H - P.t - P.b) * (1 - v / max);
  const avg = shaped.map((_, i) => { const sl = shaped.slice(Math.max(0, i - 4), i + 1); return sl.reduce((a, b) => a + b, 0) / sl.length; });

  const gridLines = [0, 1, 2, 3, 4].map(g => {
    const yy = P.t + ((H - P.t - P.b) * g / 4);
    return (
      <g key={g}>
        <line x1={P.l} x2={W - P.r} y1={yy} y2={yy} stroke="#F1F5F9" strokeWidth="1" />
        <text x={P.l - 5} y={yy + 3} textAnchor="end" fontSize="9" fill={C.ink5}
          style={{ fontFamily: f.mono }}>{Math.round(max * (1 - g / 4))}</text>
      </g>
    );
  });

  const xLabels = data.map((p, i) => {
    if (i % 5 !== 0 && i !== data.length - 1) return null;
    return <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="9" fill={C.ink5}
      style={{ fontFamily: f.mono }}>{p.date}</text>;
  });

  let chartEl;
  if (style === 'bars') {
    const bw = (W - P.l - P.r) / shaped.length * 0.6;
    chartEl = shaped.map((v, i) => {
      const h = (H - P.t - P.b) * (v / max);
      return <rect key={i} x={x(i) - bw / 2} y={H - P.b - h} width={bw} height={h} rx="2"
        fill="url(#bgrad)" />;
    });
  } else if (style === 'line') {
    const pts = shaped.map((v, i) => `${x(i)},${y(v)}`).join(' ');
    chartEl = (
      <g>
        <polyline points={pts} fill="none" stroke={C.indigo} strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" />
        {shaped.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r="2.2"
          fill="#fff" stroke={C.indigo} strokeWidth="1.5" />)}
      </g>
    );
  } else {
    const pts = shaped.map((v, i) => `${x(i)},${y(v)}`).join(' ');
    const area = `${P.l},${H - P.b} ${pts} ${x(shaped.length - 1)},${H - P.b}`;
    chartEl = (
      <g>
        <polygon points={area} fill="url(#agrad)" />
        <polyline points={pts} fill="none" stroke={C.indigo} strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" />
      </g>
    );
  }

  const avgPts = avg.map((v, i) => `${x(i)},${y(v)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
      <defs>
        <linearGradient id="agrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={C.indigo} stopOpacity=".28" />
          <stop offset="1" stopColor={C.indigo} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="bgrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#6366F1" />
          <stop offset="1" stopColor={C.indigo600} />
        </linearGradient>
      </defs>
      {gridLines}
      {xLabels}
      {chartEl}
      <polyline points={avgPts} fill="none" stroke={C.ink5}
        strokeDasharray="3 4" strokeWidth="1.4" />
    </svg>
  );
}

/* ─── pill badge ─────────────────────────────────────────────────── */
function Pill({ type = 'neutral', children }) {
  const styles = {
    ok: { bg: C.emerald50, color: C.emerald, border: '#A7F3D0' },
    warn: { bg: C.amber50, color: C.amber, border: '#FDE68A' },
    danger: { bg: C.rose50, color: C.rose, border: '#FECDD3' },
    neutral: { bg: C.surface2, color: C.ink3, border: C.line },
    indigo: { bg: C.indigo50, color: C.indigo, border: '#C7D2FE' },
  };
  const s = styles[type] || styles.neutral;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px',
      borderRadius: 999, fontSize: 10.5, fontWeight: 500,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      fontFamily: f.body,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
      {children}
    </span>
  );
}

/* ─── KPI tile ───────────────────────────────────────────────────── */
const KPI_ICONS = {
  total: { bg: C.indigo50, color: C.indigo },
  active: { bg: C.emerald50, color: C.emerald },
  expired: { bg: C.rose50, color: C.rose },
  warn: { bg: C.amber50, color: C.amber },
  cal: { bg: '#F5F3FF', color: '#7C3AED' },
  week: { bg: '#ECFEFF', color: '#0891B2' },
  month: { bg: '#EFF6FF', color: '#2563EB' },
};

function KpiTile({ label, value, delta, deltaType, icon, sparkPoints, sparkColor }) {
  const ico = KPI_ICONS[icon] || KPI_ICONS.total;
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10,
      padding: '12px 13px', position: 'relative', overflow: 'hidden',
      boxShadow: C.shadowSm, flex: 1, minWidth: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: C.ink4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', fontFamily: f.body }}>{label}</span>
        <span style={{ width: 22, height: 22, borderRadius: 6, background: ico.bg, color: ico.color, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <IcoSvg name={icon} size={12} />
        </span>
      </div>
      <div style={{ fontFamily: f.display, fontSize: 26, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1, color: C.ink1 }}>{fmtNum(value)}</div>
      {delta && (
        <div style={{
          fontSize: 10.5, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4,
          color: deltaType === 'up' ? C.emerald : deltaType === 'down' ? C.rose : C.ink4,
          fontFamily: f.mono,
        }}>{delta}</div>
      )}
      {sparkPoints && sparkPoints.length > 0 && <Spark points={sparkPoints} color={sparkColor || C.indigo} />}
    </div>
  );
}

/* ─── inline SVG icons ───────────────────────────────────────────── */
function IcoSvg({ name, size = 13, color = 'currentColor' }) {
  const s = { width: size, height: size };
  const icons = {
    total: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/></svg>,
    active: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2"><path d="m5 12 5 5L20 7"/></svg>,
    expired: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M4.5 4.5 19.5 19.5"/></svg>,
    warn: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M12 9v5"/><path d="M12 17h.01"/><path d="m10.3 3.9-8.2 14.2A2 2 0 0 0 3.8 21h16.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>,
    cal: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/></svg>,
    week: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/><path d="M8 14h2M14 14h2M8 18h2"/></svg>,
    month: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="4" width="3" height="14"/></svg>,
    trend: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="m3 17 6-6 4 4 8-8"/><path d="M14 7h7v7"/></svg>,
    people: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    check: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></svg>,
    refresh: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>,
    export: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/><path d="M12 15V3"/></svg>,
    plus: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>,
    dashboard: <svg {...s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>,
  };
  return icons[name] || null;
}

/* ─── segmented control ──────────────────────────────────────────── */
function Segmented({ options, value, onChange }) {
  return (
    <div style={{
      display: 'inline-flex', background: C.surface2, border: `1px solid ${C.line}`,
      borderRadius: 7, padding: 2, gap: 2,
    }}>
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)} style={{
          padding: '3px 9px', fontSize: 11, fontWeight: 500, borderRadius: 5,
          background: value === opt.value ? C.surface : 'none',
          color: value === opt.value ? C.ink1 : C.ink4,
          boxShadow: value === opt.value ? C.shadowSm : 'none',
          border: 0, cursor: 'pointer', fontFamily: f.body, transition: '.12s',
        }}>{opt.label}</button>
      ))}
    </div>
  );
}

/* ─── btn ─────────────────────────────────────────────────────────── */
function Btn({ primary, ghost, icon, onClick, children, spinning }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 11px',
      borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: '.12s',
      border: ghost ? 'none' : `1px solid ${primary ? C.indigo : C.line}`,
      background: primary ? C.indigo : ghost ? 'none' : C.surface,
      color: primary ? '#fff' : ghost ? C.ink4 : C.ink2,
      boxShadow: primary ? '0 1px 2px rgba(79,70,229,.3)' : 'none',
      fontFamily: f.body,
    }}>
      {icon && <span style={spinning ? { display: 'inline-block', animation: 'lm-spin .9s linear infinite' } : {}}>{icon}</span>}
      {children}
    </button>
  );
}

/* ─── section card ───────────────────────────────────────────────── */
function SectionCard({ variant, head, sub, subtabs, activeSubtab, onSubtab, children }) {
  const borderColor = variant === 'alert' ? '#FECDD3' : variant === 'warn' ? '#FDE68A' : C.line;
  const bgGrad = variant === 'alert'
    ? 'linear-gradient(180deg,#FFF1F2 0%, #FFFFFF 40px)'
    : variant === 'warn'
    ? 'linear-gradient(180deg,#FFFBEB 0%, #FFFFFF 40px)'
    : C.surface;

  return (
    <div style={{
      background: bgGrad, border: `1px solid ${borderColor}`, borderRadius: 10,
      marginBottom: 10, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '14px 16px', borderBottom: `1px solid ${borderColor}`,
      }}>
        {head}
      </div>
      {sub && <div style={{ fontSize: 11.5, color: C.ink4, padding: '8px 16px 4px' }}>{sub}</div>}
      {subtabs && (
        <div style={{ display: 'flex', gap: 2, padding: '0 16px', borderBottom: `1px solid ${C.line2}` }}>
          {subtabs.map(st => (
            <button key={st.value} onClick={() => onSubtab(st.value)} style={{
              padding: '8px 12px', fontSize: 11.5, fontWeight: 500, border: 0, background: 'none',
              cursor: 'pointer', borderBottom: `2px solid ${activeSubtab === st.value ? C.indigo : 'transparent'}`,
              color: activeSubtab === st.value ? C.indigo : C.ink4, display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: -1, fontFamily: f.body, transition: '.12s',
            }}>
              {st.label}
              <span style={{
                fontFamily: f.mono, fontSize: 10, padding: '0 6px',
                background: activeSubtab === st.value ? C.indigo50 : C.surface2,
                borderRadius: 999, color: activeSubtab === st.value ? C.indigo : C.ink4, fontWeight: 600,
              }}>{st.count}</span>
            </button>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

/* ─── empty state ─────────────────────────────────────────────────── */
function Empty({ title, sub }) {
  return (
    <div style={{ padding: '32px 16px', textAlign: 'center', color: C.ink4 }}>
      <IcoSvg name="check" size={32} color={C.emerald} />
      <div style={{ fontFamily: f.display, fontWeight: 600, fontSize: 13, color: C.ink2, marginTop: 8, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 11.5 }}>{sub}</div>
    </div>
  );
}

/* ─── table helpers ──────────────────────────────────────────────── */
const TH = ({ children, right }) => (
  <th style={{
    textAlign: right ? 'right' : 'left', fontSize: 10.5, color: C.ink4, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '.05em', padding: '8px 10px',
    borderBottom: `1px solid ${C.line}`, background: '#FCFCFB', fontFamily: f.body,
  }}>{children}</th>
);
const TD = ({ children, right, mono, client }) => (
  <td style={{
    padding: '9px 10px', borderBottom: `1px solid ${C.line2}`, verticalAlign: 'middle',
    textAlign: right ? 'right' : 'left',
    fontFamily: mono ? f.mono : f.body,
    fontSize: mono ? 11 : 12,
    color: client ? C.ink1 : mono ? C.ink3 : C.ink2,
    fontWeight: client ? 500 : 400,
  }}>{children}</td>
);

function HighFreqTable({ items }) {
  if (!items?.length) return <Empty title="No high-frequency validations" sub="All licenses are within normal thresholds." />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead><tr>
        <TH>ID</TH><TH>Client</TH><TH>Application</TH><TH>IP</TH><TH right>Validations</TH>
      </tr></thead>
      <tbody>
        {items.map((r, i) => (
          <tr key={i} style={{ cursor: 'default' }}>
            <TD mono>#{String(r.licenseId).padStart(4, '0')}</TD>
            <TD client>{r.clientName || '—'}</TD>
            <TD><Pill type="indigo">{r.appName || '—'}</Pill></TD>
            <TD mono>{r.count}</TD>
            <TD right><Pill type="danger">{fmtNum(r.count)}</Pill></TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InactiveTable({ items, onRemind, onOpen }) {
  if (!items?.length) return <Empty title="No inactive licenses" sub="All licenses have validated in this period." />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead><tr>
        <TH>ID</TH><TH>Client</TH><TH>Application</TH><TH>Expiration</TH><TH right>Actions</TH>
      </tr></thead>
      <tbody>
        {items.map((r, i) => {
          const dl = daysLeft(r.expirationDate);
          const isExp = dl !== null && dl <= 0;
          const isSoon = dl !== null && dl > 0 && dl <= 30;
          return (
            <tr key={i}>
              <TD mono>#{String(r.licenseId).padStart(4, '0')}</TD>
              <TD client>{r.clientName || '—'}</TD>
              <TD><Pill type="indigo">{r.appName || '—'}</Pill></TD>
              <TD mono>
                <span style={{ color: isExp ? C.rose : isSoon ? C.amber : C.ink3 }}>
                  {fmtDate(r.expirationDate)}
                </span>
                {isExp && <> <Pill type="danger">Expired</Pill></>}
                {isSoon && <> <Pill type="warn">{dl}d</Pill></>}
              </TD>
              <TD right>
                <div style={{ display: 'inline-flex', gap: 4, opacity: 0 }} className="lm-row-actions">
                  <Btn ghost onClick={() => onRemind && onRemind(`Reminder sent for ${r.clientName || `#${r.licenseId}`}`)}>Remind</Btn>
                  <Btn ghost onClick={() => onOpen && onOpen(r)}>Open</Btn>
                </div>
              </TD>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ExpiringTable({ items }) {
  if (!items?.length) return <Empty title="No licenses expiring soon" sub="All licenses have more than 30 days until expiration." />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead><tr>
        <TH>ID</TH><TH>Client</TH><TH>Application</TH><TH>Expiration</TH><TH right>Days Left</TH>
      </tr></thead>
      <tbody>
        {items.map((r, i) => (
          <tr key={i}>
            <TD mono>#{String(r.licenseId).padStart(4, '0')}</TD>
            <TD client>{r.clientName || '—'}</TD>
            <TD><Pill type="indigo">{r.appName || '—'}</Pill></TD>
            <TD mono>{fmtDate(r.expirationDate)}</TD>
            <TD right>
              <Pill type={r.daysLeft <= 7 ? 'danger' : r.daysLeft <= 14 ? 'warn' : 'neutral'}>
                {r.daysLeft}d
              </Pill>
            </TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ─── donut legend row ───────────────────────────────────────────── */
function DLegend({ segments, total }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {segments.map((s, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 11.5, padding: '4px 0',
          borderBottom: i < segments.length - 1 ? `1px dashed ${C.line2}` : 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.ink2 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: s.c, flexShrink: 0 }} />
            {s.k}
          </div>
          <span style={{ fontFamily: f.mono, color: C.ink3, fontWeight: 500 }}>
            {fmtNum(s.v)}
            <span style={{ color: C.ink5, marginLeft: 6 }}>
              {total ? Math.round(s.v / total * 100) : 0}%
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── chart stat bar ─────────────────────────────────────────────── */
function ChartStats({ items }) {
  return (
    <div style={{ display: 'flex', gap: 22, marginBottom: 10 }}>
      {items.map((s, i) => (
        <div key={i}>
          <div style={{ fontFamily: f.display, fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', color: s.color || C.ink1 }}>{s.v}</div>
          <div style={{ fontSize: 10.5, color: C.ink4, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 500 }}>{s.k}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── bar list ────────────────────────────────────────────────────── */
function BarList({ items }) {
  if (!items?.length) return <div style={{ padding: 16, color: C.ink5, fontSize: 12 }}>No data</div>;
  const max = Math.max(...items.map(x => x.count), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {items.map((x, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 44px', gap: 10, alignItems: 'center', fontSize: 11.5 }}>
          <div style={{
            fontFamily: f.mono, fontSize: 10.5, color: C.ink2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }} title={x.clientName}>{x.clientName || `#${x.licenseId}`}</div>
          <div style={{ height: 18, background: C.surface2, borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
            <div style={{
              position: 'absolute', inset: '0 auto 0 0',
              width: `${(x.count / max) * 100}%`,
              background: `linear-gradient(90deg,${C.indigo},#6366F1)`,
              borderRadius: 4,
            }} />
          </div>
          <div style={{ textAlign: 'right', fontFamily: f.mono, color: C.ink3, fontSize: 11, fontWeight: 500 }}>
            {fmtNum(x.count)}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── card container ──────────────────────────────────────────────── */
function Card({ children, style }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10,
      padding: '14px 16px', boxShadow: C.shadowSm, ...style,
    }}>
      {children}
    </div>
  );
}

function CardHead({ title, tag, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: f.display, fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.01em', color: C.ink1 }}>
        {title}
        {tag && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: C.surface2, color: C.ink4, fontWeight: 500, letterSpacing: '.03em', textTransform: 'uppercase', fontFamily: f.body }}>{tag}</span>}
      </div>
      {actions}
    </div>
  );
}

/* ─── section title helper ────────────────────────────────────────── */
function SectionTitle({ bgColor, color, iconName, children, count, countBg, countColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: f.display, fontWeight: 600, fontSize: 13.5 }}>
      <span style={{ width: 24, height: 24, borderRadius: 6, background: bgColor, color: color, display: 'grid', placeItems: 'center' }}>
        <IcoSvg name={iconName} size={13} />
      </span>
      {children}
      <span style={{ fontFamily: f.mono, fontSize: 11, padding: '1px 7px', borderRadius: 999, fontWeight: 600, background: countBg, color: countColor }}>{count}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                    */
/* ══════════════════════════════════════════════════════════════════ */
export default function DashboardTab({ apiBase, token, onNavigate, onNewLicense, onStatsLoaded }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [range, setRange] = useState('30');
  const [chartStyle, setChartStyle] = useState('area');
  const [freqTab, setFreqTab] = useState('today');
  const [inactiveTab, setInactiveTab] = useState('today');
  const [spinning, setSpinning] = useState(false);
  const [toast, setToast] = useState(null);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }
  const wrapRef = useRef(null);
  const onStatsLoadedRef = useRef(onStatsLoaded);
  useEffect(() => { onStatsLoadedRef.current = onStatsLoaded; });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/dashboard/stats?period=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Dashboard verileri alınamadı');
      const data = await res.json();
      setStats(data);
      if (onStatsLoadedRef.current) onStatsLoadedRef.current(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, token, range]);

  useEffect(() => { if (apiBase && token) fetchStats(); }, [fetchStats]);

  const handleRefresh = () => {
    setSpinning(true);
    fetchStats().finally(() => setTimeout(() => setSpinning(false), 700));
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      <div style={{ width: 36, height: 36, border: `3px solid ${C.line}`, borderTopColor: C.indigo, borderRadius: '50%', animation: 'lm-spin .9s linear infinite' }} />
    </div>
  );

  if (error) return (
    <div style={{ maxWidth: 600, margin: '40px auto', padding: '0 20px' }}>
      <div style={{ background: C.rose50, border: `1px solid #FECDD3`, borderRadius: 10, padding: '14px 16px', color: C.rose, fontSize: 13 }}>{error}</div>
    </div>
  );

  if (!stats) return null;

  const { overview, alerts, charts } = stats;

  /* build trend data */
  const trendData = (charts.validationsTrend || []).map(t => ({ date: t.date ? t.date.slice(5) : '', count: Number(t.count) }));
  const trendTotal = trendData.reduce((s, d) => s + d.count, 0);
  const trendPeak = trendData.reduce((p, d) => d.count > p.count ? d : p, { date: '—', count: 0 });

  /* overview stats are from dedicated DB queries — use them for Total/Avg instead of trend sum */
  const displayTotal = range === '7'
    ? overview.totalValidationsWeek
    : range === '30'
    ? overview.totalValidationsMonth
    : trendTotal; // 90d: no direct overview equivalent, fall back to trend sum
  const displayAvg = displayTotal > 0 ? (displayTotal / Number(range)).toFixed(1) : '0';
  const sparkPoints = (charts.validationsTrend || []).map(t => t.count);

  /* status donut */
  const STATUS_COLOR_MAP = { ok: C.emerald, ok_global: '#2196F3', ok_with_warning: C.amber, expired: C.rose, not_found: '#9C27B0', denied_global: '#795548' };
  const STATUS_LABELS = { ok: 'OK', ok_global: 'OK (Global)', ok_with_warning: 'OK (Warning)', expired: 'Expired', not_found: 'Not Found', denied_global: 'Denied' };
  const STATUS_ORDER = ['ok', 'ok_global', 'ok_with_warning', 'expired', 'not_found', 'denied_global'];
  const statusCountMap = Object.fromEntries((charts.statusDistribution || []).map(s => [s.status, Number(s.count)]));
  const statusSegs = STATUS_ORDER.map(key => ({ k: STATUS_LABELS[key], v: statusCountMap[key] || 0, c: STATUS_COLOR_MAP[key] }));
  const statusTotal = statusSegs.reduce((s, x) => s + x.v, 0);

  /* app donut */
  const APP_COLORS = [C.indigo, '#10B981', C.amber, '#7C3AED', C.rose, '#0891B2'];
  const appSegs = (charts.licensesByApp || []).map((a, i) => ({ k: a.name || 'Unknown', v: Number(a.count), c: APP_COLORS[i % APP_COLORS.length] }));
  const appTotal = appSegs.reduce((s, x) => s + x.v, 0);

  /* high-freq subtab data */
  const freqData = { today: alerts.frequentDay || [], week: alerts.frequentWeek || [], month: alerts.frequentMonth || [] };
  const freqSubtabs = [
    { value: 'today', label: 'Today', count: freqData.today.length },
    { value: 'week', label: 'This Week', count: freqData.week.length },
    { value: 'month', label: 'This Month', count: freqData.month.length },
  ];

  /* inactive subtab data */
  const inactData = { today: alerts.inactiveToday || [], week: alerts.inactiveWeek || [], month: alerts.inactiveMonth || [] };
  const inactSubtabs = [
    { value: 'today', label: 'Today', count: inactData.today.length },
    { value: 'week', label: 'This Week', count: inactData.week.length },
    { value: 'month', label: 'This Month', count: inactData.month.length },
  ];

  const totalHighFreq = freqData.today.length + freqData.week.length + freqData.month.length;

  return (
    <>
      <style>{`
        @keyframes lm-spin { to { transform: rotate(360deg); } }
        .lm-row-actions { opacity: 0 !important; transition: .12s; }
        tr:hover .lm-row-actions { opacity: 1 !important; }
        tr:hover td { background: #FDFCFB; }
        table tr:last-child td { border-bottom: 0 !important; }
      `}</style>

      <div ref={wrapRef} style={{ maxWidth: 1440, margin: '0 auto', padding: '18px 20px 60px', background: C.bg, minHeight: '100%' }}>

        {/* page header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11.5, color: C.ink5, marginBottom: 3 }}>
              License Manager &nbsp;/&nbsp; <b style={{ color: C.ink3, fontWeight: 500 }}>Dashboard</b>
            </div>
            <h1 style={{ fontFamily: f.display, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.02em', color: C.ink1 }}>Dashboard</h1>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Segmented
              options={[{ value: '7', label: '7d' }, { value: '30', label: '30d' }, { value: '90', label: '90d' }]}
              value={range}
              onChange={setRange}
            />
            <Btn ghost icon={<IcoSvg name="export" size={13} />}>Export</Btn>
            <Btn icon={<IcoSvg name="refresh" size={13} />} onClick={handleRefresh} spinning={spinning}>Refresh</Btn>
            <Btn primary icon={<IcoSvg name="plus" size={13} />} onClick={() => onNewLicense && onNewLicense()}>New License</Btn>
          </div>
        </div>

        {/* KPI tiles */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <KpiTile label="Total Licenses" value={overview.totalLicenses} icon="total" delta="+2 vs last month" deltaType="up" sparkPoints={sparkPoints.slice(-8)} />
          <KpiTile label="Active" value={overview.activeLicenses} icon="active" delta={`${overview.totalLicenses > 0 ? Math.round(overview.activeLicenses / overview.totalLicenses * 100) : 0}% healthy`} deltaType="up" />
          <KpiTile label="Expired" value={overview.expiredLicenses} icon="expired" delta={overview.expiredLicenses === 0 ? 'no expiries' : `${overview.expiredLicenses} total`} />
          <KpiTile label="Expiring < 30d" value={overview.expiringSoon} icon="warn" delta={overview.expiringSoon === 0 ? 'no action required' : 'action needed'} deltaType={overview.expiringSoon > 0 ? 'down' : undefined} />
          <KpiTile label="Validations Today" value={overview.totalValidationsToday} icon="cal" sparkPoints={sparkPoints.slice(-4)} sparkColor="#7C3AED" />
          <KpiTile label="Validations · Week" value={overview.totalValidationsWeek} icon="week" sparkPoints={sparkPoints.slice(-7)} sparkColor="#0891B2" />
          <KpiTile label="This Month" value={overview.totalValidationsMonth} icon="month" delta="+4.1% MoM" deltaType="up" sparkPoints={sparkPoints} sparkColor="#2563EB" />
        </div>

        {/* Trend + Status row */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
          <Card>
            <CardHead
              title="Validation Trend"
              tag={`Last ${range} Days`}
              actions={
                <Segmented
                  options={[{ value: 'area', label: 'Area' }, { value: 'line', label: 'Line' }, { value: 'bars', label: 'Bars' }]}
                  value={chartStyle}
                  onChange={setChartStyle}
                />
              }
            />
            <ChartStats items={[
              { v: fmtNum(displayTotal), k: 'Total' },
              { v: displayAvg, k: 'Daily Avg' },
              { v: fmtNum(trendPeak.count), k: `Peak · ${trendPeak.date}` },
              { v: statusTotal > 0 ? `${((statusSegs.find(s => s.k === 'OK')?.v || 0) / statusTotal * 100).toFixed(1)}%` : '—', k: 'Success Rate', color: C.emerald },
            ]} />
            <TrendChart data={trendData} style={chartStyle} width={wrapRef.current ? Math.floor(wrapRef.current.clientWidth * 0.62) : 700} />
            <div style={{ display: 'flex', gap: 14, fontSize: 11, color: C.ink4, marginTop: 8 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: C.indigo }} />Validations</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: C.ink5, border: `1px dashed ${C.ink5}` }} />30-day avg</span>
            </div>
          </Card>

          <Card>
            <CardHead title="Status Distribution" tag={`Last ${range} Days`} />
            {statusSegs.length === 0
              ? <div style={{ padding: '32px 0', textAlign: 'center', color: C.ink5, fontSize: 12 }}>No data</div>
              : <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                  <Donut segments={statusSegs} centerV={fmtNum(statusTotal)} centerK="Total" />
                  <DLegend segments={statusSegs} total={statusTotal} />
                </div>
            }
          </Card>
        </div>

        {/* Top licenses + App distribution row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 10, marginBottom: 10 }}>
          <Card>
            <CardHead title="Top Validated Licenses" tag={`Last ${range} Days`} actions={<Btn ghost onClick={() => onNavigate && onNavigate('history')}>View all →</Btn>} />
            <BarList items={charts.topValidatedLicenses || []} />
          </Card>

          <Card>
            <CardHead title="Licenses by Application" />
            {appSegs.length === 0
              ? <div style={{ padding: '32px 0', textAlign: 'center', color: C.ink5, fontSize: 12 }}>No data</div>
              : <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                  <Donut segments={appSegs} centerV={fmtNum(appTotal)} centerK="Licenses" />
                  <DLegend segments={appSegs} total={appTotal} />
                </div>
            }
          </Card>
        </div>

        {/* Expiring section */}
        <SectionCard
          variant="neutral"
          head={
            <SectionTitle bgColor={C.amber50} color={C.amber} iconName="warn"
              count={alerts.expiringSoon?.length || 0}
              countBg={C.surface2} countColor={C.ink4}>
              Licenses Expiring Within 30 Days
            </SectionTitle>
          }
        >
          <ExpiringTable items={alerts.expiringSoon || []} />
        </SectionCard>

        {/* High-frequency section */}
        <SectionCard
          variant="alert"
          head={
            <SectionTitle bgColor={C.rose50} color={C.rose} iconName="trend"
              count={totalHighFreq}
              countBg={totalHighFreq > 0 ? C.rose : C.surface2}
              countColor={totalHighFreq > 0 ? '#fff' : C.ink4}>
              High-Frequency Validations
            </SectionTitle>
          }
          sub="Licenses exceeding validation thresholds · Day >3 · Week >10 · Month >30"
          subtabs={freqSubtabs}
          activeSubtab={freqTab}
          onSubtab={setFreqTab}
        >
          <HighFreqTable items={freqData[freqTab]} />
        </SectionCard>

        {/* No validations section */}
        <SectionCard
          variant="warn"
          head={
            <SectionTitle bgColor={C.amber50} color={C.amber} iconName="people"
              count={inactData[inactiveTab]?.length || 0}
              countBg={inactData[inactiveTab]?.length > 0 ? C.amber : C.surface2}
              countColor={inactData[inactiveTab]?.length > 0 ? '#fff' : C.ink4}>
              Licenses With No Validations
            </SectionTitle>
          }
          sub="Licenses that exist but have not validated in the selected period (up to 20 shown)"
          subtabs={inactSubtabs}
          activeSubtab={inactiveTab}
          onSubtab={setInactiveTab}
        >
          <InactiveTable items={inactData[inactiveTab]} onRemind={showToast} onOpen={(r) => onNavigate && onNavigate('licenses', { licenseId: r.licenseId })} />
        </SectionCard>

      </div>
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: C.ink1, color: '#fff', padding: '10px 16px',
          borderRadius: 8, fontSize: 12, fontFamily: f.body,
          boxShadow: C.shadow,
        }}>{toast}</div>
      )}
    </>
  );
}
